/**
 * Viewport overlay layout for the Gleamer viewer.
 *
 * `GleamerViewportOverlay` owns both bottom corners: bottom-left carries
 * Sex / Age / SliceThickness / Study date, bottom-right carries Laterality /
 * View. Upstream's `viewportOverlay.*` defaults (cornerstone extension) put
 * their own items in those same corners, so the two overlays render on top of
 * each other, and upstream's top-left `StudyDate` repeats the date the Gleamer
 * overlay already shows.
 *
 * That is what the 3.13 review reported (DATAT-482): a duplicated study date,
 * and W / L / I sitting at the bottom superimposed on other text.
 *
 * So: keep the upstream *items*, but move the ones we still want to the free
 * corners (top) and leave the bottom corners to the Gleamer overlay. The item
 * definitions below are upstream's, only relocated — see
 * `extensions/cornerstone/src/customizations/viewportOverlayCustomization.tsx`.
 *
 * NOTE: changing this changes every viewport visual-regression baseline. The
 * `draw-mammo` / `save-pass` snapshots encode the overlay text, so they must be
 * regenerated (`--env pluginVisualRegressionUpdateImages=true`) whenever the
 * layout below moves.
 *
 * GLEAMER FORK PATCH (re-apply on merge): re-check this against upstream's
 * defaults after every OHIF merge — new default items land in the bottom
 * corners and will collide again.
 */
export default {
  'viewportOverlay.topLeft': [
    {
      id: 'SeriesDescription',
      inheritsFrom: 'ohif.overlayItem',
      label: '',
      title: 'Series description',
      condition: ({ referenceInstance }) =>
        referenceInstance && referenceInstance.SeriesDescription,
      contentF: ({ referenceInstance }) => referenceInstance.SeriesDescription,
    },
    {
      id: 'WindowLevel',
      inheritsFrom: 'ohif.overlayItem.windowLevel',
      title: 'Window Level',
    },
    {
      id: 'ZoomLevel',
      inheritsFrom: 'ohif.overlayItem.zoomLevel',
      condition: props => {
        const activeToolName = props.toolGroupService.getActiveToolForViewport(
          props.viewportId
        );
        return activeToolName === 'Zoom';
      },
    },
  ],
  'viewportOverlay.topRight': [
    {
      id: 'InstanceNumber',
      inheritsFrom: 'ohif.overlayItem.instanceNumber',
      title: 'Instance Number',
    },
  ],
  // Owned by GleamerViewportOverlay — anything registered here draws on top of it.
  'viewportOverlay.bottomLeft': [],
  'viewportOverlay.bottomRight': [],
};
